/**
 * Sidebar — role-aware, contextual workspace navigation.
 *
 * Two completely separate role experiences (no Frankenstein):
 *
 *  ADMIN (multi-tenant operator) — three tiers, one visible at a time:
 *    Tier 0, Global rail (persistent):  Deals, Facilities, Borrowers, Alerts
 *    Tier 1, Borrower workspace (in a borrower/book): KYC header + BOOK sections + FACILITIES
 *    Tier 2, Facility workspace (in a facility): breadcrumb + facility sections  (replaces tier 1)
 *
 *  BORROWER (single-tenant) — flat portal, no multi-tenant chrome:
 *    Rail by permission: Facilities, Book, Upload, Team. Inside their book → curated
 *    book sections. Inside a facility they hold → the reading of the facility
 *    (NON_ADMIN_FACILITY_NAV), never its configuration or its files, and never the
 *    write controls inside a section they do read.
 *
 * Borrower context is bound to the URL scope, never to the persisted store alone
 * (otherwise a previously-visited borrower leaks onto the landing).
 */

import { Link, useLocation } from "react-router-dom";
import cn from "classnames";
import { useEffect } from "react";
import { useFacilityStore } from "@/stores/useFacilityStore";
import { usePortfolioStore } from "@/stores/usePortfolioStore";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { useBorrowerStore } from "@/stores/useBorrowerStore";
import { usePermissions, hasDealsAccess, isAnonymous, type UserPermissions } from "@/hooks/usePermissions";
import { api } from "@/lib/api";
import { ANON_FACILITY_NAV, FACILITY_NAV, NON_ADMIN_FACILITY_NAV, resolveSection } from "@/pages/facility/facility.constants";
import type { FacilityInfo } from "@/pages/facility/facility.types";
import s from "../sidebar.module.sass";
import { BorrowerHeader } from "./BorrowerHeader";
import { BookGroup } from "./BookGroup";
import { CollapsibleGroup } from "./CollapsibleGroup";
import { NAV_ICON, SECTION_ICON } from "./sidebar.icons";
import type { BorrowerCtx, SidebarBorrower, SidebarDeal, SidebarFacility, SidebarMetrics } from "./types";

type Scope = "facility" | "workspace" | "global";

/**
 * Map a standalone facility sub-route (/facility/:id/<seg>) to the cockpit section it
 * belongs to, or null when it is no section at all. These routes carry no
 * ?section=, so resolveSection's "overview" fallback would falsely highlight
 * Overview while the user sits on a different page.
 */
function subRouteSection(seg: string): string | null {
  // The pre-HU-046 wizard URL survives only as a redirect to config (App.tsx).
  const id = seg === "setup" ? "config" : seg;
  return FACILITY_NAV.items.some((i) => i.id === id) ? id : null;
}


export function Sidebar({ collapsed = false }: { collapsed?: boolean }) {
  const location = useLocation();
  const perms = usePermissions();

  const facilityId = matchFacilityId(location.pathname);
  const urlBorrowerId = matchBorrowerId(location.pathname);
  const urlBookId = matchBookId(location.pathname);

  const sbBorrower = useBorrowerStore((st) => st.borrower);
  const sbBooks = useBorrowerStore((st) => st.books);
  const sbFacilities = useBorrowerStore((st) => st.facilities);
  const sbDeals = useBorrowerStore((st) => st.deals);

  const facility = useFacilityStore((st) => st.facility);
  const metrics = usePortfolioStore((st) => st.metrics) as SidebarMetrics;
  const alertCount = useGlobalStore((st) => st.unacknowledgedAlertCount);
  const incidentCount = useGlobalStore((st) => st.activeIncidentCount);
  const borrowerCount = useGlobalStore((st) => st.borrowerCount);
  const applicationNewCount = useGlobalStore((st) => st.applicationNewCount);

  // Admin drill-down: populate the borrower store when on a facility URL.
  // (Book pages load the borrower store themselves; borrower-detail pages too.)
  useEffect(() => {
    if (!facilityId || !perms.isAdmin) return;
    if (facility?.borrower?.id) {
      useBorrowerStore.getState().load(facility.borrower.id);
    } else {
      api.getFacility(facilityId).then((d: FacilityInfo) => {
        if (d?.borrower?.id) useBorrowerStore.getState().load(d.borrower.id);
      }).catch(() => {});
    }
  }, [facilityId, facility?.borrower?.id, perms.isAdmin]);

  const isAdmin = perms.isAdmin;
  const canBook = isAdmin || perms.bookView;
  const scope: Scope =
    facilityId ? "facility" : (urlBookId || urlBorrowerId) ? "workspace" : "global";

  // Borrower context is bound to the URL scope. Outside an object route we
  // deliberately ignore the persisted store so a stale borrower can't leak.
  const borrowerCtx =
    scope === "global" ? null : buildBorrowerCtx({ sbBorrower, sbBooks, sbFacilities, facilityId, facility, urlBorrowerId });

  const effectiveBookId = urlBookId ?? borrowerCtx?.activeBook?.id ?? perms.bookId ?? null;

  const searchParams = new URLSearchParams(location.search);
  // On /facility/:id the section lives in ?section= — resolve legacy ids so the
  // active highlight matches the section FacilityPage will redirect to (HU-062.1).
  // Standalone sub-routes (/facility/:id/config, /setup) instead carry the section
  // in the path; anything there that is not a cockpit section leaves nothing
  // active rather than falling back to Overview. Section links stay visible and
  // clickable either way: a DRAFT facility has portfolio-only data worth previewing.
  const facilitySubRoute = location.pathname.match(/^\/facility\/[^/]+\/([^/]+)/)?.[1] ?? null;
  const activeSection = facilitySubRoute
    ? subRouteSection(facilitySubRoute)
    : resolveSection(searchParams.get("section"));
  const bookTab = searchParams.get("tab") || "overview";

  return (
    <nav className={s.nav}>
      {/* ── Tier 0, Global rail ── */}
      <GlobalZone
        perms={perms}
        pathname={location.pathname}
        alertCount={alertCount}
        incidentCount={incidentCount}
        borrowerCount={borrowerCount}
        applicationNewCount={applicationNewCount}
        hasFacilities={hasDealsAccess(perms)}
        collapsed={collapsed}
      />

      {/* ── Tier 2, Facility workspace ──
           Admin sees the full facility nav; everybody else sees the sections
           their facility grant opens. */}
      {!collapsed && scope === "facility" && facilityId && (
        <FacilityContext
          facilityId={facilityId}
          facilityName={facility?.facility_name ?? "Facility"}
          borrower={borrowerCtx?.borrower ?? null}
          bookId={borrowerCtx?.activeBook?.id ?? null}
          isAdmin={isAdmin}
          anonymous={isAnonymous(perms, facility?.book_id ?? null)}
          activeSection={activeSection}
          covenantStatus={metrics?.covenant_status ?? null}
        />
      )}

      {/* ── Tier 1, Borrower workspace (book + facilities) ── */}
      {!collapsed && scope === "workspace" && (
        <div className={s.sectionDivider}>
          {borrowerCtx && <BorrowerHeader borrower={borrowerCtx.borrower} activeBook={borrowerCtx.activeBook} />}

          {canBook && effectiveBookId && (
            <BookGroup
              bookId={effectiveBookId}
              currentBookId={urlBookId}
              activeTab={bookTab}
              isAdmin={isAdmin}
              defaultOpen
            />
          )}

          {/* Facilities are part of the admin's borrower workspace. A borrower reaches
              their facilities through the flat rail, never as a facility sub-tree here. */}
          {isAdmin && borrowerCtx && (
            <FacilitiesList
              facilities={borrowerCtx.facilities}
              deals={sbDeals}
              borrowerId={borrowerCtx.borrower.id}
            />
          )}

          {isAdmin && effectiveBookId && (
            <Link
              to={`/books/${effectiveBookId}/team`}
              className={cn(s.sectionNavLink, { [s.active]: location.pathname === `/books/${effectiveBookId}/team` })}
            >
              {SECTION_ICON.team}Team Access
            </Link>
          )}

          {/* Borrower record (KYC / danger zone), reachable after the book redirect. */}
          {isAdmin && borrowerCtx && (
            <Link to={`/borrowers/${borrowerCtx.borrower.id}?manage=1`} className={s.sectionNavLink}>
              {SECTION_ICON.settings}Borrower settings
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}

/* ── Tier 0, Global rail ── */

function GlobalZone({ perms, pathname, alertCount, incidentCount, borrowerCount, applicationNewCount, hasFacilities, collapsed }: {
  perms: UserPermissions;
  pathname: string;
  alertCount: number;
  incidentCount: number;
  borrowerCount: number;
  applicationNewCount: number;
  hasFacilities: boolean;
  collapsed: boolean;
}) {
  if (perms.isAdmin) {
    // The rail reflects the top-level you're under, even when deep:
    //   a facility sits under Facilities; a book/borrower sits under Borrowers.
    const inFacilities = pathname === "/" || pathname.startsWith("/facility");
    // `/deals` (the index) and `/deal/:id` (one contract) both light this one.
    // Until A8 a contract page lit nothing in the rail, because there was
    // nowhere for it to sit.
    const inDeals = pathname.startsWith("/deal");
    const inBorrowers = pathname.startsWith("/borrowers") || pathname.startsWith("/books");
    return (
      <div>
        <Link to="/overview" className={cn(s.navItem, { [s.active]: pathname === "/overview" })} title={collapsed ? "Overview" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.overview}{!collapsed && <span>Overview</span>}</span>
        </Link>
        {/* Contracts above mechanisms, which is the hierarchy: a facility only
            means something under a Deal. */}
        <Link to="/deals" className={cn(s.navItem, { [s.active]: inDeals })} title={collapsed ? "Deals" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.deals}{!collapsed && <span>Deals</span>}</span>
        </Link>
        <Link to="/" className={cn(s.navItem, { [s.active]: inFacilities })} title={collapsed ? "Facilities" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.facilities}{!collapsed && <span>Facilities</span>}</span>
        </Link>
        <Link to="/borrowers" className={cn(s.navItem, { [s.active]: inBorrowers })} title={collapsed ? "Borrowers" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.borrowers}{!collapsed && <span>Borrowers{borrowerCount > 0 ? ` (${borrowerCount})` : ""}</span>}</span>
        </Link>
        <Link
          to="/incidents"
          className={cn(s.navItem, { [s.active]: pathname === "/incidents" })}
          title={collapsed ? "Incidents" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.incidents}{!collapsed && <span>Incidents</span>}</span>
          {incidentCount > 0 && (collapsed ? <span className={s.badgeDot} /> : <span className={s.badge}>{incidentCount}</span>)}
        </Link>
        <Link
          to="/applications"
          className={cn(s.navItem, { [s.active]: pathname === "/applications" })}
          title={collapsed ? "Applications" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.applications}{!collapsed && <span>Applications</span>}</span>
          {applicationNewCount > 0 && (collapsed ? <span className={s.badgeDot} /> : <span className={s.badge}>{applicationNewCount}</span>)}
        </Link>
        <Link
          to="/alerts"
          className={cn(s.navItem, { [s.active]: pathname === "/alerts" })}
          title={collapsed ? "Alerts" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.alerts}{!collapsed && <span>Alerts</span>}</span>
          {alertCount > 0 && (collapsed ? <span className={s.badgeDot} /> : <span className={s.badge}>{alertCount}</span>)}
        </Link>
        <Link
          to="/admin/system"
          className={cn(s.navItem, { [s.active]: pathname === "/admin/system" })}
          title={collapsed ? "System" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.settings}{!collapsed && <span>System</span>}</span>
        </Link>
      </div>
    );
  }

  // Borrower — flat permitted destinations, no multi-tenant chrome.
  return (
    <div>
      {hasFacilities && (
        <Link to="/" className={cn(s.navItem, { [s.active]: pathname === "/" })} title={collapsed ? "Facilities" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.facilities}{!collapsed && <span>Facilities</span>}</span>
        </Link>
      )}
      {perms.books.filter((b) => b.bookView).map((b) => (
        <Link
          key={b.bookId}
          to={`/books/${b.bookId}`}
          className={cn(s.navItem, { [s.active]: pathname === `/books/${b.bookId}` || pathname.startsWith(`/books/${b.bookId}/`) })}
          title={collapsed ? (perms.books.length > 1 ? b.bookName ?? "Book" : "Book") : undefined}
        >
          <span className={s.navLabel}>
            {NAV_ICON.book}
            {!collapsed && <span>{perms.books.length > 1 ? b.bookName ?? "Book" : "Book"}</span>}
          </span>
        </Link>
      ))}
      {perms.bookUpload && (
        <Link
          to="/borrower/upload"
          className={cn(s.navItem, { [s.active]: pathname === "/borrower/upload" })}
          title={collapsed ? "Upload Files" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.upload}{!collapsed && <span>Upload Files</span>}</span>
        </Link>
      )}
      {perms.manageAccess && (
        <Link
          to="/borrower/team"
          className={cn(s.navItem, { [s.active]: pathname === "/borrower/team" })}
          title={collapsed ? "Team Access" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.team}{!collapsed && <span>Team Access</span>}</span>
        </Link>
      )}
    </div>
  );
}

/* ── Tier 2, Facility workspace ── */

function FacilityContext({ facilityId, facilityName, borrower, bookId, isAdmin, anonymous, activeSection, covenantStatus }: {
  facilityId: string;
  facilityName: string;
  borrower: SidebarBorrower | null;
  bookId: string | null;
  isAdmin: boolean;
  anonymous: boolean;
  activeSection: string | null;
  covenantStatus: string | null | undefined;
}) {
  // Admin sees the full facility cockpit nav; anybody holding the facility reads the
  // facility. A guest under an anonymous grant loses the two sections whose
  // payloads name the originator with no anonymous form (HU-066). What none of
  // them get is the write surface inside a section — that is each section's own
  // `canManage`, not a nav decision.
  const items = (isAdmin ? FACILITY_NAV : anonymous ? ANON_FACILITY_NAV : NON_ADMIN_FACILITY_NAV).items;

  // Up-navigation lands on the borrower's home — its book (the workspace).
  const upHref = bookId ? `/books/${bookId}?tab=snapshot` : borrower ? `/borrowers/${borrower.id}?manage=1` : "/borrowers";

  return (
    <div className={s.sectionDivider}>
      {/* Breadcrumb up to the borrower workspace (admin only). */}
      {isAdmin && borrower && (
        <Link to={upHref} className={s.breadcrumb}>
          ‹ {borrower.legal_name}
        </Link>
      )}

      <div className={s.facilityBlock}>
        <div className={s.facilityName}>{facilityName}</div>
        {/* `legal_name` is already the alias under an anonymous grant, the
            server redacted it, so this line needs no branch. */}
        {!isAdmin && borrower && <div className={s.facilityMeta}>{borrower.legal_name}</div>}
      </div>

      {items.map((item) => {
        const isActive = activeSection === item.id;
        const attention =
          (item.id === "covenants" || item.id === "overview") &&
          (covenantStatus === "BREACHED" || covenantStatus === "WARNING");
        return (
          <Link
            key={item.id}
            to={item.ready ? `/facility/${facilityId}?section=${item.id}` : "#"}
            onClick={(e) => { if (!item.ready) e.preventDefault(); }}
            className={cn(s.navItem, s.facilitySubItem, {
              [s.active]: isActive,
              [s.disabled]: !item.ready,
            })}
          >
            <span className={s.navLabel}><span>{item.label}</span></span>
            {attention && (
              <span className={cn(s.dot, covenantStatus === "BREACHED" ? s.breach : s.warn)} />
            )}
          </Link>
        );
      })}
    </div>
  );
}

/* ── Facilities list inside the borrower workspace (admin) ──
 *
 * Grouped by Deal, because the Deal is the contract and a facility only means
 * something under one: two facilities of the same contract are one negotiation,
 * two facilities of different contracts are two. Each group heads with a link
 * to the contract and offers the alta of another facility under it — which is
 * also how the alta gets its `deal_id` without a picker.
 *
 * A facility whose Deal is not in the list (a book of another borrower, a
 * contract the reader cannot see) still gets a group, headed by nothing but its
 * id: dropping it would hide a facility, which is worse than an ugly heading.
 */

function FacilitiesList({ facilities, deals, borrowerId }: {
  facilities: SidebarFacility[];
  deals: SidebarDeal[];
  borrowerId: string;
}) {
  const byDeal = new Map<string, SidebarFacility[]>();
  for (const f of facilities) {
    const list = byDeal.get(f.deal_id) ?? [];
    list.push(f);
    byDeal.set(f.deal_id, list);
  }
  const dealsById = new Map(deals.map((d) => [d.id, d]));

  return (
    <CollapsibleGroup label={`FACILITIES (${facilities.length})`} icon={SECTION_ICON.facilities} defaultOpen>
      {/* No covenant dot here: this list comes from `GET /facilities`, which
          carries no metrics. The dot was rendered off a field the endpoint has
          never sent, so it never appeared. Restoring it means reading the
          status from the `/facilities/summary` store, which is a decision, not
          a repair. */}
      {[...byDeal.entries()].map(([dealId, dealFacilities]) => {
        const deal = dealsById.get(dealId);
        return (
          <div key={dealId} className={s.dealGroup}>
            <Link to={`/deal/${dealId}`} className={s.dealGroupHead}>
              {deal?.deal_name ?? deal?.deal_reference ?? `Deal ${dealId.slice(0, 8)}`}
            </Link>
            {dealFacilities.map((d) => (
              <Link key={d.id} to={`/facility/${d.id}`} className={s.navItem}>
                <span className={s.navLabel}><span>{d.facility_name}</span></span>
              </Link>
            ))}
            <Link to={`/facility/new?deal=${dealId}`} className={s.navItem}>
              <span className={s.navLabel}><span>+ Add facility</span></span>
            </Link>
          </div>
        );
      })}
      <Link to={`/deal/new?borrower=${borrowerId}`} className={s.navItem}>
        <span className={s.navLabel}><span>+ New deal</span></span>
      </Link>
    </CollapsibleGroup>
  );
}

/* ── URL parsing ── */

function matchFacilityId(pathname: string): string | null {
  const m = pathname.match(/^\/facility\/([^/]+)/);
  const id = m?.[1];
  return id && id !== "new" ? id : null;
}

function matchBorrowerId(pathname: string): string | null {
  return pathname.match(/^\/borrowers\/([^/?]+)/)?.[1] ?? null;
}

function matchBookId(pathname: string): string | null {
  return pathname.match(/^\/books\/([^/?]+)/)?.[1] ?? null;
}

/* ── Borrower context assembly ── */

function buildBorrowerCtx({
  sbBorrower,
  sbBooks,
  sbFacilities,
  facilityId,
  facility,
  urlBorrowerId,
}: {
  sbBorrower: ReturnType<typeof useBorrowerStore.getState>["borrower"];
  sbBooks: ReturnType<typeof useBorrowerStore.getState>["books"];
  sbFacilities: ReturnType<typeof useBorrowerStore.getState>["facilities"];
  facilityId: string | null;
  facility: FacilityInfo | null;
  urlBorrowerId: string | null;
}): BorrowerCtx | null {
  // Trust the store only when it matches the URL's borrower (avoids stale leak).
  const storeMatchesUrl = !urlBorrowerId || sbBorrower?.id === urlBorrowerId;
  if (sbBorrower && storeMatchesUrl) {
    return {
      borrower: sbBorrower,
      activeBook: (sbBooks[0] ?? null) as BorrowerCtx["activeBook"],
      facilities: sbFacilities as BorrowerCtx["facilities"],
    };
  }
  // Partial context while the borrower store loads — only facility data available.
  if (facilityId && facility?.borrower) {
    return {
      borrower: {
        id: facility.borrower.id,
        legal_name: facility.borrower.legal_name,
        country: facility.borrower.country,
        // The chip states the borrower's operating currency: that is the book's,
        // not the facility's, which is only a reporting preference.
        currency: facility.book_currency || facility.currency,
        kyc_status: facility.borrower.kyc_status,
      } as SidebarBorrower,
      activeBook: null,
      facilities: [],
    };
  }
  return null;
}
